import React, { useEffect, useState } from 'react';
import ProcessingActionsHeader from './ProcessingActionsHeader';
import { ActionsType, BulkOperation, ProcessingAction, StateChange } from 'types/rest/assetCatalogTypes';
import axios from 'axios/axios-base';
import _ from 'lodash';
import { useIsMount } from 'hooks/useIsMountHook';
import ProcessingActionModal, { TriggerTime } from './ProcessingActionModal';
import i18n from '../../../../../i18n';
import { confirm } from '../../../../../components/ui/modal/BlazeConfirmationModal';
import { ProcessingActionsProvider } from '../../../../../contexts/ProcessingActionContext';
import { useDispatch } from 'react-redux';
import { showToastNotification } from '../../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../../store/ui/toasts/toastsTypes';
import { Condition } from '../../../../../types/rest/dslTypes';
import { transformConditionsToBase64 } from '../../../../../utils/transactionUtils';
import ProcessingActionsTable from './Table/ProcessingActionsTable';
import { PairSetItem } from '../../../../../components/pairSet/PairSetList';

export type ProcessingActionTableOptions = {
  isSimple: boolean;
  isSelectable: boolean;
  columns: {
    name: boolean;
    type: boolean;
    conditions: boolean;
    actions: boolean;
  };
};

type ProcessingActionsProps = {
  serviceId: number;
  transactionId: number;
  isHeaderHidden?: boolean;
  tableOptions?: ProcessingActionTableOptions;
};

const webhookProcessingAction: ProcessingAction = {
  actionType: 'WEBHOOK',
  name: '',
  definition: {
    urlMethod: 'GET',
    urlValue: '',
    triggerTime: TriggerTime.SPECIFIC,
    specific: 0,
    lower: 1,
    upper: 10,
    headers: [] as PairSetItem[],
    queryParams: [] as PairSetItem[],
    bodyContent: '',
  },
};

const stateUpdateProcessingAction: ProcessingAction = {
  actionType: 'STATE_UPDATE',
  name: '',
  definition: {
    objectAction: 'STORE_OBJECT',
    model: '',
    parameters: [],
    filters: [],
  } as StateChange,
};

const httpCallProcessingAction: ProcessingAction = {
  actionType: 'HTTP_CALL',
  name: '',
  definition: {
    urlMethod: 'GET',
    urlValue: '',
    headers: [] as PairSetItem[],
    queryParams: [] as PairSetItem[],
    bodyContent: '',
  },
};

const ProcessingActions = (props: ProcessingActionsProps) => {
  /**
   * Goes thru all processing actions and build a unique list of available conditions.
   */
  const buildUniqueListOfConditions = (actions: ProcessingAction[]) => {
    let conditions = [];
    _.forEach(actions, (action: ProcessingAction) => {
      if (!_.isNil(action.conditions)) {
        conditions = _.concat(conditions, action.conditions);
      }
    });
    _.uniqBy(conditions, ['matcher.key', 'matcher.matcherName', 'matcher.matchingValue']);
    setTxConditions(conditions);
  };

  /**
   * Gets list of processing actions for this transaction.
   */
  const getProcessingActions = async () => {
    const response = await axios.get(`workspaces/{workspaceId}/transactions/${props.transactionId}/actions?sort=-id`);
    let processingActions: ProcessingAction[] = _.orderBy(response.data.result, ['priority', 'id'], 'asc');

    //Decode conditions from base64
    processingActions = _.forEach(processingActions, pa => {
      pa.conditions = transformConditionsToBase64(pa.conditions, 'base64decode');
    });

    //Count actions by type
    const counts = _.countBy(processingActions, 'actionType');

    //Prepare list of conditions
    buildUniqueListOfConditions(processingActions);
    setActionCounts(counts);
    setActions(processingActions);
    setLoading(false);
  };

  const isMount = useIsMount();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isMount) {
      getProcessingActions().then();
    }
  }, []);

  const [actions, setActions] = useState([] as ProcessingAction[]);

  /** List of all conditions across all processing actions. */
  const [txConditions, setTxConditions] = useState([] as Condition[]);
  const [actionCounts, setActionCounts] = useState<{ [key: string]: number | null }>(null);
  const [loading, setLoading] = useState(true);
  const [selectedActions, setSelectedActions] = useState<number[]>([]);
  /** Processing action in "focus" for modal */
  const [processingAction, setProcessingAction] = useState<ProcessingAction>();

  const onSelectActionHandler = (selectedItems: Array<any>) => {
    setSelectedActions(selectedItems);
  };

  const onCreateHandler = (type: ActionsType) => {
    switch (type) {
      case 'WEBHOOK':
        setProcessingAction({ ...webhookProcessingAction, transactionId: props.transactionId } as ProcessingAction);
        break;
      case 'STATE_UPDATE':
        setProcessingAction({ ...stateUpdateProcessingAction, transactionId: props.transactionId } as ProcessingAction);
        break;
      case 'HTTP_CALL':
        setProcessingAction({ ...httpCallProcessingAction, transactionId: props.transactionId } as ProcessingAction);
        break;
    }
  };

  const onDeleteHandler = (processingAction?: ProcessingAction) => {
    let modalBody;
    const isSingleAction = !_.isNil(processingAction);
    if (isSingleAction) {
      modalBody = (
        <div>
          {i18n.t('transactions:table.bulkDeleteWarn')}
          <strong className="text-break ml-2">{processingAction?.name}</strong>?
        </div>
      );
    } else {
      modalBody = (
        <div>
          {i18n.t('transactions:table.bulkDeleteWarn')}
          <strong className="text-break ml-1 mr-2">{selectedActions.length}</strong>
          {selectedActions.length === 1
            ? i18n.t('transactions:processingActions.deleteConfirmationSingular')
            : i18n.t('transactions:processingActions.deleteConfirmationPlural')}
          ?
        </div>
      );
    }

    confirm({
      confirmation: modalBody,
      yesOptionTitle: i18n.t('common:actions.delete'),
      title: i18n.t('transactions:processingActions.deleteTitle'),
    })
      .then(async () => {
        if (isSingleAction) {
          await deleteProcessingActions([processingAction?.id as number]);
          dispatch(
            showToastNotification(ToastType.Success, 'Successfully deleted Processing Action ' + processingAction.name),
          );
        } else {
          await deleteProcessingActions(selectedActions);
          dispatch(
            showToastNotification(
              ToastType.Success,
              `Successfully deleted ${selectedActions.length} processing actions`,
            ),
          );
        }
        //Refresh the list after deletion & delete list of selected
        setSelectedActions([]);
        getProcessingActions();
      })
      .catch(() => {
        // User chose "No"
      });
  };

  const onEditHandler = (processingAction: ProcessingAction) => {
    setProcessingAction(processingAction);
  };

  const onCloseModal = () => {
    setProcessingAction(undefined);
  };

  const deleteProcessingActions = async (processingActionsIds: number[]) => {
    const bulkOperation: BulkOperation = {
      action: 'DELETE',
      ids: processingActionsIds,
    };
    await axios.patch(`workspaces/{workspaceId}/transactions/${props.transactionId}/actions`, bulkOperation);
  };

  //Call the backend
  const onDragFinishedHandler = async (items: any[]) => {
    try {
      const orderedItems = items.map((item, index) => {
        return { id: item.id, priority: index + 1 };
      });
      await axios.post(`workspaces/{workspaceId}/transactions/${props.transactionId}/actions/sort`, orderedItems);
      dispatch(showToastNotification(ToastType.Success, 'Order Updated. Your new arrangement is now in effect.'));
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot save order of processing actions.'));
    }
  };

  // Just safe null check, should never happen
  if (_.isNil(props.transactionId)) {
    return <div>No Transaction specified</div>;
  }

  return (
    <ProcessingActionsProvider serviceId={props.serviceId}>
      <div className={'bg-light p-4'}>
        {!_.isNil(processingAction) && (
          <ProcessingActionModal
            onCreateFinished={() => getProcessingActions()}
            onClose={onCloseModal}
            processingAction={processingAction}
            txConditions={txConditions}
          />
        )}

        {props.isHeaderHidden !== true && (
          <ProcessingActionsHeader
            totalCount={actions.length}
            actionCounts={actionCounts}
            numberOfSelected={selectedActions.length}
            onDelete={onDeleteHandler}
            onCreate={onCreateHandler}
          />
        )}

        <ProcessingActionsTable
          tableOptions={props.tableOptions}
          onEdit={onEditHandler}
          onSelect={onSelectActionHandler}
          processingActionList={actions}
          deleteAction={onDeleteHandler}
          isLoading={loading}
          onDragFinished={onDragFinishedHandler}
        />
      </div>
    </ProcessingActionsProvider>
  );
};

export default ProcessingActions;
